// @spec AUD-STR-021
// Evidence upload page route

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { EvidenceUploadForm } from '@/features/evidence/components/EvidenceUploadForm';
import type { Evidence } from '@/features/evidence/types';

const searchParamsSchema = z.object({
  linkedObjectType: z.string().optional(),
  linkedObjectId: z.string().optional(),
  complianceContext: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/audit/evidence-upload')({
  validateSearch: searchParamsSchema,
  component: EvidenceUploadPage,
});

function EvidenceUploadPage() {
  const navigate = useNavigate();
  const { linkedObjectType, linkedObjectId, complianceContext } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  const handleUploadComplete = (evidence: Evidence) => {
    if (linkedObjectType === 'training_record' && linkedObjectId) {
      void navigate({
        to: '/workforce-readiness/training-register/$trainingId',
        params: { trainingId: linkedObjectId },
      });
      return;
    }

    if (linkedObjectType === 'awareness_campaign' && linkedObjectId) {
      void navigate({
        to: '/workforce-readiness/awareness-campaigns/$campaignId',
        params: { campaignId: linkedObjectId },
      });
      return;
    }

    if (linkedObjectType === 'asset' && linkedObjectId) {
      void navigate({
        to: '/assets/$assetId',
        params: { assetId: linkedObjectId },
      });
      return;
    }

    if (linkedObjectType === 'risk' && linkedObjectId) {
      void navigate({
        to: '/risks/$riskId',
        params: { riskId: linkedObjectId },
      });
      return;
    }

    if (linkedObjectType === 'vulnerability' && linkedObjectId) {
      void navigate({
        to: '/vulnerabilities/$vulnerabilityId',
        params: { vulnerabilityId: linkedObjectId },
      });
      return;
    }

    if (linkedObjectType === 'policy' && linkedObjectId) {
      void navigate({
        to: '/policies/$policyId',
        params: { policyId: linkedObjectId },
      });
      return;
    }

    if (linkedObjectType === 'control') {
      void navigate({
        to: '/controls',
      });
      return;
    }

    void navigate({
      to: '/audit/evidence/$evidenceId',
      params: { evidenceId: evidence.id },
    });
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold">Upload Compliance Evidence</h1>
          <p className="mt-2 text-muted-foreground">
            Upload documents, screenshots, or other files to document compliance with regulatory
            requirements. Evidence files will be stored securely and linked to your audit trail.
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <EvidenceUploadForm
          onUploadComplete={handleUploadComplete}
          onUploadError={handleUploadError}
          {...(linkedObjectType ? { defaultLinkedObjectType: linkedObjectType } : {})}
          {...(linkedObjectId ? { defaultLinkedObjectId: linkedObjectId } : {})}
          {...(complianceContext ? { defaultComplianceContext: complianceContext } : {})}
        />
      </div>
    </div>
  );
}
